// Product Review tab: the publish workflow of a product. The organization
// sends it for review and sees where it stands; a platform admin approves or
// rejects it. Where it stands is the pill under the dialog's title
// (PublishStateSubtitle), shown whatever tab is open.
import { useUrlParam } from "aldoo-ra/URLState"
import React, { useEffect, useState } from "react"
import Button from "aldoo-ra/Button"
import Typography from "aldoo-ra/Typography"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { showNotification } from "aldoo-ra/Notification"
import { ReviewProduct, GetVendorProfile } from "../../../AdminShared/api"
import { eligibilityOf } from "../../../Vendor/EligibilityChecklist"
import ContractRequiredDialog from "../../../Vendor/ContractRequiredDialog"
import { errorMessage } from "../../../AdminShared/ui"
import { ExclamationTriangleIcon, ClockIcon } from "@heroicons/react/24/outline"
import { CheckCircleIcon, PaperAirplaneIcon, XCircleIcon } from "@heroicons/react/24/outline"
import { HelperText } from "../shared/shared"
import { EPublishStatus } from "../shared/editor-config"
import useProductEditorTexts from "aldoo-ra/CMSModels/StoreProduct/useProductEditorTexts"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"

const stateHelp = {
  [EPublishStatus.Draft]: "Only the people who edit it can see this product. Send it for review when it is ready.",
  [EPublishStatus.InReview]: "Waiting for a platform admin to approve or reject it.",
  [EPublishStatus.Published]: "Approved. Whether the store offers it is the Enabled switch on Availability.",
  [EPublishStatus.Rejected]: "Rejected. Send it for review again once the notes are addressed.",
  [EPublishStatus.ReviewRequired]: "A reviewer asks for changes. Make them, then send it for review again.",
}

// what the review workflow's refusals mean here
const REFUSED = {
  already_in_review: "It is already waiting for review.",
  already_published: "It is published already.",
  not_in_review: "It is not waiting for review.",
  access_denied: "That is not yours to do.",
}

// the state each action leaves the product in
const NEXT = {
  submit: EPublishStatus.InReview,
  withdraw: EPublishStatus.Draft,
  approve: EPublishStatus.Published,
  reject: EPublishStatus.Rejected,
}

const DONE = {
  submit: "Sent for review",
  withdraw: "Taken back from review",
  approve: "Approved",
  reject: "Rejected",
}

// a decision notice's status, as the product's state
const notify = (message, isError = false) =>
  showNotification({ message, className: isError ? "bg-admin_error text-white" : "bg-admin_success text-white" })

export default function AdminReviewTab({ editor, isAdmin, isProductEditor }) {
  const { read, adopt, product } = editor
  const t = useProductEditorTexts()
  const status = read("publishStatus") ?? EPublishStatus.Draft
  const name = read("name") || read("_id")
  const inReview = status === EPublishStatus.InReview
  const published = status === EPublishStatus.Published
  const canSubmit = status === EPublishStatus.Draft || status === EPublishStatus.Rejected || status === EPublishStatus.ReviewRequired
  // sent back with what to change: the marks not yet changed hold it back (the API refuses it too)
  const unchanged = status === EPublishStatus.ReviewRequired && !isAdmin ? (product?.lastReview?.attention || []).filter((item) => !item.resolved) : []
  // what goes with the next action: notes for the reviewer, or the
  // reviewer's message to the vendor
  const [message, setMessage] = useState("")
  const [busy, setBusy] = useState("")

  // what the company still lacks to sell (its eligibility checklist): a product goes for review
  // only once its company is ready
  const orgId = product?.publisher ? String(product.publisher?._id || product.publisher) : null
  const [missing, setMissing] = useState(null)
  // a new payout contract waiting for the company's signature: Send opens ContractRequiredDialog instead
  const [contractWaiting, setContractWaiting] = useState(false)
  const [contractStop, setContractStop] = useState(false)
  useEffect(() => {
    if (!orgId || !isProductEditor) return
    let alive = true
    GetVendorProfile(orgId)
      .then((res) => {
        if (!alive || !res?.org) return
        setContractWaiting(!!res.payouts?.contractPending)
        // a new contract waiting is not a missing detail here: the Send button stops on it with its own dialog
        setMissing(eligibilityOf(res.org, { physical: !!res.hasPhysicalProducts, payouts: res.payouts, items: res.eligibility }).filter((item) => !item.optional && !item.done && !(item.key === "payoutContract" && res.payouts?.contractPending)))
      })
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [orgId, isProductEditor])
  // an Aldoo admin sends on the company's behalf whatever its set-up (the API lets them: a demo, helping it along)
  const notReady = !isAdmin && (missing?.length || 0) > 0

  // sent from the dialog's header (?psubmit=1, PublishStateSubtitle): once the company's readiness is known,
  // sent as the button below sends it — what holds it back (company details, marks unchanged) shows here instead
  const [psubmit, setPsubmit] = useUrlParam("psubmit", { default: "" })
  useEffect(() => {
    if (!psubmit || !isProductEditor || !product?.pid) return
    if (orgId && missing === null) return
    setPsubmit("")
    if (!canSubmit || notReady || unchanged.length || busy) return
    if (contractWaiting && !isAdmin) setContractStop(true)
    else act("submit")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [psubmit, missing, product?.pid])

  // what happens to this product elsewhere (a reviewer's answer, the vendor sending it) reaches the dialog
  // through its header (shared/useReviewSync): the editor's review fields, which this tab reads, follow

  // Each button is an action of the review workflow, done now — not a field
  // saved with the dialog: the product's state, the submission the reviewers
  // see and the letters change together. The editor takes the new state
  // without marking anything to save.
  const act = async (action) => {
    setBusy(action)
    try {
      const res = await ReviewProduct(product.pid, action, message.trim())
      const failed = errorMessage(res)
      if (failed) return notify(REFUSED[failed] ? t(REFUSED[failed]) : failed, true)
      adopt("publishStatus", NEXT[action])
      setMessage("")
      notify(t(DONE[action]))
    } catch (error) {
      notify(error?.message || t`Could not change the review state`, true)
    } finally {
      setBusy("")
    }
  }

  const reject = () =>
    showPromptDialog({
      variant: "admin",
      title: t`Reject this product?`,
      message: published
        ? t("%s goes back to its organization and leaves the store: its page answers that it is not available, and it cannot be bought. Sending it for review again is up to them.", name)
        : t("%s goes back to its organization. Sending it for review again is up to them.", name),
      confirmText: t`Reject`,
      cancelText: t`Cancel`,
      onConfirm: () => act("reject"),
    })

  // taking it back drops its place in the queue: asked first
  const withdraw = () =>
    showPromptDialog({
      variant: "admin",
      title: t`Cancel the review?`,
      message: t("%s leaves the review queue and goes back to drafts. You can send it for review again when it is ready.", name),
      confirmText: t`Cancel Review`,
      cancelText: t`Keep in review`,
      onConfirm: () => act("withdraw"),
    })

  // the vendor's product waiting for Aldoo: where it stands, and only the way to take it back
  if (inReview && isProductEditor && !isAdmin) {
    return (
      <div className="max-w-[760px]">
        <div className="flex flex-wrap items-center gap-4 rounded-lg border border-admin_warning/40 bg-admin_warning/10 px-4 py-3" data-testid="review-pending">
          <ClockIcon className="w-6 h-6 shrink-0 text-admin_warning" />
          <div className="flex-1 min-w-[220px]">
            <div className="text-sm font-semibold text-admin_text">{t`Waiting for review`}</div>
            <div className="text-sm text-admin_text_secondary">{t(stateHelp[EPublishStatus.InReview])} <HelpHint helpKey="vendor.product.setup#admin-review" /></div>
          </div>
          <Button variant="admin_secondary" className="px-4 h-[34px] text-xs" disabled={!!busy} onClick={withdraw} data-testid="withdraw-review">
            {busy === "withdraw" ? t`Cancelling…` : t`Cancel Review`}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6 max-w-[760px]">
      <ContractRequiredDialog open={contractStop} orgId={orgId} onClose={() => setContractStop(false)} />
      <HelperText>{t(stateHelp[status] ?? stateHelp[EPublishStatus.Draft])} <HelpHint helpKey="vendor.product.setup#admin-review" /></HelperText>

      {(isProductEditor || isAdmin) && (
        <textarea
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          rows={3}
          placeholder={isAdmin ? t`A message to the vendor with the decision (optional)` : t`Anything the reviewer should know (optional)`}
          className="w-full px-3 py-2 rounded-md border border-admin_bordered bg-admin_paper text-admin_text text-sm outline-none focus:border-admin_primary"
          data-testid="review-message"
        />
      )}

      {/* sent back by a reviewer: what they marked and said, until the product is sent again */}
      {(status === EPublishStatus.Rejected || status === EPublishStatus.ReviewRequired) && product?.lastReview && (product.lastReview.attention?.length > 0 || product.lastReview.notes) && (
        <div className="flex flex-col gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="review-what-to-change">
          <div className="font-semibold">{t`What the reviewer asks you to change`}</div>
          {product.lastReview.attention?.length > 0 ? (
            <ul className="m-0 pl-5 flex flex-col gap-1">
              {product.lastReview.attention.map((item) => (
                <li key={item.id} className={item.resolved ? "text-emerald-700" : ""} data-resolved={item.resolved ? "true" : "false"}>
                  {item.resolved && <CheckCircleIcon className="inline w-4 h-4 mr-1 -mt-0.5" />}
                  <b className={item.resolved ? "line-through decoration-1" : ""}>{t(item.label)}</b>
                  {item.note && <span className={item.resolved ? "line-through decoration-1" : ""}>: {item.note}</span>}
                </li>
              ))}
            </ul>
          ) : null}
          {/* what they said of the whole (beside the marks), or all they said (a plain rejection) */}
          {(product.lastReview.attention?.length > 0 ? product.lastReview.message : product.lastReview.notes) && (
            <p className="m-0 whitespace-pre-line">{product.lastReview.attention?.length > 0 ? product.lastReview.message : product.lastReview.notes}</p>
          )}
          <div className="text-xs">{unchanged.length ? t`Change each marked item; then it can be sent for review again.` : t`Make the changes, then send it for review again.`}</div>
        </div>
      )}

      {/* the company is not ready to sell: said here, with the way to its checklist */}
      {isProductEditor && canSubmit && notReady && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900" role="alert" data-testid="review-not-ready">
          <ExclamationTriangleIcon className="w-5 h-5 shrink-0 text-amber-500 mt-0.5" />
          <div className="min-w-0">
            <div className="font-semibold">{t`Your company details are incomplete, so this product cannot be sent for review yet.`}</div>
            <div className="mt-0.5">{t("Missing: %s.", missing.map((item) => t(item.label)).join(", "))}</div>
            <a href={`${typeof window !== "undefined" && /^\/[a-z]{2}(\/|$)/.test(window.location.pathname) ? window.location.pathname.slice(0, 3) : ""}/vendor?org=${orgId}&section=dashboard&checklist=open`} className="inline-block mt-1 font-semibold underline" data-testid="review-open-checklist">
              {t`Open the company checklist`}
            </a>
          </div>
        </div>
      )}

      {isProductEditor && (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="admin_primary"
            className="px-4 h-[34px] text-xs"
            disabled={!canSubmit || !!busy || notReady || unchanged.length > 0}
            title={notReady ? t`Complete your company details first` : unchanged.length ? t`Change what the reviewer marked first` : undefined}
            onClick={() => (contractWaiting && !isAdmin ? setContractStop(true) : act("submit"))}
            data-testid="send-for-review"
          >
            <PaperAirplaneIcon className="w-4 h-4" /> {busy === "submit" ? t`Sending…` : t`Send for Review`}
          </Button>
          {inReview && (
            <Button
              variant="admin_secondary"
              className="px-4 h-[34px] text-xs"
              disabled={!!busy}
              onClick={withdraw}
              data-testid="withdraw-review"
            >
              {t`Withdraw`}
            </Button>
          )}
        </div>
      )}

      {/* approval is the platform admin's */}
      {isAdmin && (
        <div className="flex flex-col gap-2 pt-2 border-t border-admin_bordered">
          <Typography component="h4" variant="admin_h4" className="my-0">{t`Decision`}</Typography>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="admin_success"
              className="px-4 h-[34px] text-xs"
              disabled={published || !!busy}
              onClick={() => act("approve")}
              data-testid="approve-product"
            >
              <CheckCircleIcon className="w-4 h-4" /> {t`Approve`}
            </Button>
            <Button
              variant="admin_error"
              className="px-4 h-[34px] text-xs"
              disabled={status === EPublishStatus.Rejected || !!busy}
              onClick={reject}
              data-testid="reject-product"
            >
              <XCircleIcon className="w-4 h-4" /> {t`Reject`}
            </Button>
          </div>
          <HelperText>{t`Takes effect at once, and the vendor is told; the message above goes with it.`}</HelperText>
        </div>
      )}
    </div>
  )
}
